import { Fragment, useCallback, useEffect, useState } from 'react';
import { HeaderAuth, MyLink, TitleAuth } from '../Login/Components';
import TwinTrans from '../../../../baseComponents/TwinTrans';
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation';
import { twinFetchPostText } from '../../../../utils/globals/data';
import { QRCodeSVG } from 'qrcode.react';
import LoadingSpinner from '../../../../baseComponents/LoaderDecider/LoadingSpinner';
import { getSocketId, listenSocket, unlistenSocket } from '../../../../utils/globals/socket';
import { setUserByLogin } from '../../../../utils/reducers/reduxDispatch';
import { useNavigate } from 'react-router';
import { TwinIconToolTip } from '../../../../baseComponents/TwinIcon';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';

export interface LoginWithMobileQrProps { }

const LoginWithMobileQr: React.FC<LoginWithMobileQrProps> = () => {
    const { t } = useTwinTranslation()
    const { authCode, verificationCode } = useLoginWithMobileQrLogic()
    const headerSubtitle = verificationCode ? t('confirmSameCodeInOtherDevice', 'Confirma que puedes ver este código en el otro dispositivo') : t('scanThisQrToEnterLong', 'Escanea este código QR desde tu aplicación móvil de Biuun para iniciar sesión directamente')
    return (
        <Fragment>
            <LoginWithMobileQrHeader />
            <div className='mt-40 text-center'>
                <span className='regular14'>
                    {headerSubtitle}
                </span>
                {!verificationCode ? <TwinIconToolTip message={t('loginWithQrExplanation', 'Accede desde tu aplicación móvil en el icono de perfil y pulsa sobre "Iniciar sesión en otro dispositivo"')} id={'help'} icon={faInfoCircle} className='inline-block ml-5' classNameTooltip='max-w-300' /> : null}
            </div>
            <LoginWithMobileQrMain authCode={authCode} verificationCode={verificationCode} />
            <div className='flex justify-center items-center mt-30 '>
                <MyLink text={t('return', 'Volver')} link={'/'} />
            </div>
        </Fragment>
    );
}

const useLoginWithMobileQrLogic = () => {
    const [authCode, setAuthCode] = useState<string | null>(null)
    const [verificationCode, setVerificationCode] = useState<string | null>(null)
    const navigate = useNavigate()
    const getAuthCode = useCallback(async () => {
        const mySocketId = getSocketId()
        if (mySocketId) {
            const result = await twinFetchPostText('/api/generateAuthCode', {
                SocketId: mySocketId
            })
            if (result) {
                setAuthCode(result)
            }
        }
    }, [setAuthCode])

    const getVerificationCode = useCallback((myVerificationCode?: string) => {
        if (myVerificationCode) {
            setVerificationCode(myVerificationCode)
        }
    }, [])

    const getCodeForLogin = useCallback(async (token?: string) => {
        if (token) {
            await setUserByLogin({ token, type: 'erp' })
            navigate('/')
        }
    }, [navigate])
    
    useEffect(() => {
        getAuthCode()
    }, [getAuthCode])

    useEffect(() => {
        if (authCode) {
            listenSocket('verification_code_ready_'+authCode, getVerificationCode, true)
            return () => {
                unlistenSocket('verification_code_ready_' + authCode)
            }
        }
    }, [authCode, getVerificationCode])

    useEffect(() => {
        if (authCode && verificationCode) {
            listenSocket('mobile_login_validate_' + authCode, getCodeForLogin, true)
            return () => {
                unlistenSocket('mobile_login_validate_' + authCode)
            }
        }
    }, [authCode, getCodeForLogin, verificationCode])

    return { authCode, verificationCode, navigate }
}

const LoginWithMobileQrHeader: React.FC = () => {
    return (
        <Fragment>
            <HeaderAuth />
            <TitleAuth>
                <TwinTrans transKey='loginPageSubtitleNew'>
                    Bienvenido a la mejor forma de gestionar tu empresa
                </TwinTrans>
            </TitleAuth>
        </Fragment>
    )
}

interface LoginWithMobileQrMainProps {
    authCode?: string | null
    verificationCode?: string | null
}

const LoginWithMobileQrMain: React.FC<LoginWithMobileQrMainProps> = ({ authCode , verificationCode}) => {
    if (verificationCode) {
        return <VerificationCodeComponent verificationCode={verificationCode}/>
    } else if (authCode) {
        return <MobileQrForLogin authCode={authCode} />
    }
    return <LoadingSpinner className='mt-30 h-250' />
}

interface VerificationCodeComponentProps {
    verificationCode: string
}

const VerificationCodeComponent: React.FC<VerificationCodeComponentProps> = ({ verificationCode }) => {
    const myVerificationCode = `${String(verificationCode)?.substring?.(0, 3)}-${String(verificationCode)?.substring?.(3, 6)}`
    return (<div className='regular40 flex justify-center flex-auto mt-30 h-250'>{myVerificationCode}</div>)
}

interface MobileQrForLoginProps {
    authCode: string
}
const MobileQrForLogin: React.FC<MobileQrForLoginProps> = ({ authCode }) => {
    return (
        <div className='flex justify-center flex-auto mt-30'>
                <div className='relative inline-block'>
                <QRCodeSVG value={authCode}
                        size={250}
                        className='qr_for_punchin'
                        imageSettings={{
                            src: "/BIUUN_ISOTIPO.svg",
                            height: 45,
                            width: 40,
                            opacity: 1,
                            excavate: true,
                        }} />
                </div>
       </div>
    )
}



export default LoginWithMobileQr;